/**
 * Audits Statistics Component
 * Displays audit statistics and metrics by season
 */

import { Card, Table, Statistic, Row, Col, Spin, Typography, Space, Select } from 'antd';
import { useState, useEffect } from 'react';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  AreaChartOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { auditsDashboardService, type AuditsDashboardData } from '../../services/api/auditsDashboardService';
import { seasonService, type Season } from '../../services/api/seasonService';
import { AuditsMonthlyChart } from './AuditsMonthlyChart';
import { AuditTypesByYearChart } from './AuditTypesByYearChart';

const { Text } = Typography;

export const AuditsStats: React.FC = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number | undefined>(undefined);
  const [dashboardData, setDashboardData] = useState<AuditsDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load seasons on mount
  useEffect(() => {
    const loadSeasons = async () => {
      try {
        const seasonsData = await seasonService.getSeasons();
        setSeasons(seasonsData);
        // Select the most recent season by default
        if (seasonsData.length > 0) {
          const sortedSeasons = [...seasonsData].sort((a, b) => b.year - a.year);
          setSelectedSeason(sortedSeasons[0].id);
        }
      } catch (error) {
        // Error handled silently
      }
    };
    loadSeasons();
  }, []);

  // Load dashboard data when season changes
  useEffect(() => {
    if (!selectedSeason) return;

    const loadDashboardData = async () => {
      setIsLoading(true);
      try {
        const data = await auditsDashboardService.getDashboardData(selectedSeason);
        setDashboardData(data);
      } catch (error) {
        // Error handled silently
      } finally {
        setIsLoading(false);
      }
    };
    loadDashboardData();
  }, [selectedSeason]);

  // Auditors table columns
  const auditorsColumns = [
    {
      title: 'Auditor',
      dataIndex: 'auditor_name',
      key: 'auditor_name',
      sorter: (a: any, b: any) => a.auditor_name.localeCompare(b.auditor_name),
    },
    {
      title: 'Nº Auditorías',
      dataIndex: 'audits_count',
      key: 'audits_count',
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.audits_count}</Text>
          {record.audits_growth !== 0 && (
            <Text
              style={{
                fontSize: 12,
                color: record.audits_growth > 0 ? '#52c41a' : '#ff4d4f',
              }}
            >
              {record.audits_growth > 0 ? '+' : ''}{record.audits_growth}%
            </Text>
          )}
        </Space>
      ),
      sorter: (a: any, b: any) => a.audits_count - b.audits_count,
    },
    {
      title: 'Superficie (ha)',
      dataIndex: 'surface',
      key: 'surface',
      align: 'right' as const,
      render: (_: any, record: any) => (
        <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
          <Text strong>{record.surface.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
          {record.surface_growth !== 0 && (
            <Text
              style={{
                fontSize: 12,
                color: record.surface_growth > 0 ? '#52c41a' : '#ff4d4f',
              }}
            >
              {record.surface_growth > 0 ? '+' : ''}{record.surface_growth}%
            </Text>
          )}
        </Space>
      ),
      sorter: (a: any, b: any) => a.surface - b.surface,
    },
    {
      title: 'Tiempo Medio',
      dataIndex: 'avg_duration_hours',
      key: 'avg_duration_hours',
      align: 'right' as const,
      render: (_: any, record: any) => {
        const hours = Math.floor(record.avg_duration_hours);
        const minutes = Math.round((record.avg_duration_hours - hours) * 60);
        return (
          <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
            <Text strong>{hours}h {minutes}m</Text>
            {record.duration_growth !== 0 && (
              <Text
                style={{
                  fontSize: 12,
                  color: record.duration_growth < 0 ? '#52c41a' : '#ff4d4f', // Invertido: menos tiempo es mejor
                }}
              >
                {record.duration_growth > 0 ? '+' : ''}{record.duration_growth}%
              </Text>
            )}
          </Space>
        );
      },
      sorter: (a: any, b: any) => a.avg_duration_hours - b.avg_duration_hours,
    },
    {
      title: 'kg/ha Medio',
      dataIndex: 'avg_kg_per_ha',
      key: 'avg_kg_per_ha',
      align: 'right' as const,
      render: (_: any, record: any) => (
        <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
          <Text strong>{record.avg_kg_per_ha.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
          {record.kg_per_ha_growth !== 0 && (
            <Text
              style={{
                fontSize: 12,
                color: record.kg_per_ha_growth > 0 ? '#52c41a' : '#ff4d4f',
              }}
            >
              {record.kg_per_ha_growth > 0 ? '+' : ''}{record.kg_per_ha_growth}%
            </Text>
          )}
        </Space>
      ),
      sorter: (a: any, b: any) => a.avg_kg_per_ha - b.avg_kg_per_ha,
    },
    {
      title: 'Última Auditoría',
      dataIndex: 'last_audit_date',
      key: 'last_audit_date',
      align: 'center' as const,
      render: (value: string | null) => value || '-',
      sorter: (a: any, b: any) => (a.last_audit_date || '').localeCompare(b.last_audit_date || ''),
    },
  ];

  return (
    <div>
      {/* Season Selector */}
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong>Seleccionar Campaña:</Text>
          <Select
            style={{ width: '100%', maxWidth: 400 }}
            placeholder="Seleccionar campaña"
            value={selectedSeason}
            onChange={setSelectedSeason}
            options={seasons.map(season => ({
              label: season.name || season.year.toString(),
              value: season.id,
            }))}
          />
        </Space>
      </Card>

      {isLoading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Cargando estadísticas de auditorías...</Text>
            </div>
          </div>
        </Card>
      ) : dashboardData ? (
        <>
          {/* KPIs */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Auditorías"
                  value={dashboardData.kpis.completed_audits}
                  prefix={<CheckCircleOutlined />}
                />
                <Text type="secondary" style={{ fontSize: 14 }}>
                  {dashboardData.kpis.completed_audits} / {dashboardData.kpis.total_audits} auditorías
                </Text>
                <br />
                <Text
                  style={{
                    fontSize: 14,
                    color: dashboardData.kpis.completion_percentage >= 80 ? '#52c41a' : '#faad14',
                  }}
                >
                  {dashboardData.kpis.completion_percentage}% completado
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Tiempo Medio"
                  value={`${Math.floor(dashboardData.kpis.avg_duration_hours)}h ${Math.round((dashboardData.kpis.avg_duration_hours - Math.floor(dashboardData.kpis.avg_duration_hours)) * 60)}m`}
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Superficie Total"
                  value={dashboardData.kpis.total_surface}
                  precision={2}
                  suffix="ha"
                  prefix={<AreaChartOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Productividad Media"
                  value={dashboardData.kpis.avg_kg_per_ha}
                  precision={2}
                  suffix="kg/ha"
                  prefix={<RiseOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Auditors Table */}
          <Card title="Estadísticas por Auditor" style={{ marginBottom: 24 }}>
            <Table
              columns={auditorsColumns}
              dataSource={dashboardData.auditors}
              rowKey="auditor_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total: ${total} auditores`,
              }}
            />
          </Card>

          {/* Charts Row */}
          <Row gutter={[16, 16]}>
            {/* Monthly Evolution */}
            {dashboardData.monthly_evolution && dashboardData.monthly_evolution.length > 0 && (
              <Col xs={24} xl={12}>
                <AuditsMonthlyChart data={dashboardData.monthly_evolution} />
              </Col>
            )}

            {/* Types by Year Chart */}
            {dashboardData.types_by_year && dashboardData.types_by_year.length > 0 && (
              <Col xs={24} xl={12}>
                <AuditTypesByYearChart data={dashboardData.types_by_year} />
              </Col>
            )}
          </Row>
        </>
      ) : (
        <Card>
          <Text type="danger">Error al cargar las estadísticas de auditorías</Text>
        </Card>
      )}
    </div>
  );
};
